import CirclePlayVideo from "@dana/Icons/CirclePlayVideo";
import NextArrowIcon from "@dana/Icons/NextArrowIcon";
import PrevArrowIcon from "@dana/Icons/PrevArrowIcon";
import { gradients, themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { elevation } from "@dana/MobiusOverrides/styles/colors";
import { fontSizes, fontWeights } from "@dana/MobiusOverrides/styles/typography";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button/Button";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import { ImgProps } from "@insite/mobius/Img/Img";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import useEmblaCarousel from "embla-carousel-react";
import React, { useCallback, useEffect, useState } from "react";
import ReactPlayer from "react-player";
import styled, { css } from "styled-components";

interface SlideModel {
    fields: {
        slideTitle: string;
        videoTitle: string;
        videoUrl: string;
        videothumbnail: string;
        buttonLabel: string;
        buttonLink: LinkFieldValue;
    };
}

const enum fields {
    slideIndicator = "slideIndicator",
    slides = "slides",
}

interface OwnProps extends WidgetProps {
    fields: {
        buttonLink: LinkFieldValue;
        buttonLabel: string;
        slideTitle: string;
        [fields.slideIndicator]: boolean;
        [fields.slides]: SlideModel[];
    };
    extendedStyles?: SlideshowStyles;
}

export interface SlideshowStyles {
    videoSliderContainerWrapper?: InjectableCss;
    slideShowContainer?: InjectableCss;
    slideshowWrapper?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    iconProps?: IconPresentationProps;
    closeModelIcon?: ButtonPresentationProps;
    carouselContainer?: InjectableCss;
    slideContainerWrapper?: InjectableCss;
    slideContentWrapper?: InjectableCss;
    slideOverlayWrapper?: InjectableCss;
    slideCenteringWrapperStyles?: InjectableCss;
    slideContent?: InjectableCss;
    subheadingText?: TypographyPresentationProps;
    videoSliderButton?: ButtonPresentationProps;
    viewAllButton?: InjectableCss;
    dotsContainerWrapper?: InjectableCss;
    nextArrowWrapper?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
    carouselArrowWrapper?: InjectableCss;
    slideContentVideoWrapper?: GridItemProps;
    slideThumbWrapper?: InjectableCss;
    thumbImage?: LazyImageProps;
    slideContentVideoTitleWrapper?: GridItemProps;
    videoSliderHeader?: InjectableCss;
    videoSliderHeading?: TypographyPresentationProps;
    videoModal?: ModalPresentationProps;
    videoModalTitle?: TypographyPresentationProps;
}

export const slideshowStyles: SlideshowStyles = {
    videoSliderContainerWrapper: {
        css: css`
            background: ${themeColors.primary.white};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 40px 16px;
                        `,
                        css`
                            padding: 64px 24px;
                        `,
                        null,
                        css`
                            padding: 64px 24px;
                        `,
                        css`
                            padding: 80px 40px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    slideShowContainer: {
        css: css`
            background: ${themeBackgrounds.lightGray};
            position: relative;
            width: 100%;
            max-width: 358px;
            flex-basis: 358px;
            padding: 24px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            max-width: 786px;
                            flex-basis: 786px;
                            padding: 32px;
                        `,
                        null,
                        null,
                        css`
                            max-width: 787px;
                            flex-basis: 787px;
                            padding: 40px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    slideshowWrapper: {
        css: css`
            position: relative;
            max-height: 100%;
            min-height: 100%;
        `,
    },
    prevArrowWrapper: {
        css: css`
            z-index: 0;
        `,
    },
    prevArrowButton: {
        shape: "rectangle",
        css: css`
            padding: 0;
            min-width: auto;
            min-height: auto;
            z-index: 2;
            justify-content: center;
            background: none;
            border: none;

            ${IconWrapper} {
                svg {
                    path {
                        fill: ${themeColors.secondary.danaDarkBlue};
                    }
                }
            }

            &:not(:disabled) {
                &:hover {
                    background: transparent;
                    border: none;

                    ${IconWrapper} {
                        svg {
                            path {
                                fill: ${themeColors.secondary.danaBrightBlue};
                            }
                        }
                    }
                }

                &:focus {
                    outline: none;
                }

                &:focus-visible {
                    background: transparent;
                    border: 1px solid ${themeColors.primary.white};
                    box-shadow: ${elevation.focus};
                    outline: none;

                    ${IconWrapper} {
                        svg {
                            path {
                                fill: ${themeColors.secondary.danaBrightBlue};
                            }
                        }
                    }
                }
            }

            &:disabled {
                background: transparent;
                ${IconWrapper} {
                    svg {
                        path {
                            fill: ${themeBackgrounds.darkGray};
                        }
                    }
                }
            }
        `,
    },
    videoModal: {
        cssOverrides: {
            modalContent: css`
                display: flex;
                flex-direction: column-reverse;
                padding: 0 24px 24px;
                padding-top: 24px;

                iframe {
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    min-height: 250px;
                                `,
                                css`
                                    min-height: 250px;
                                `,
                                css`
                                    min-height: 350px;
                                `,
                                css`
                                    min-height: 350px;
                                `,
                                css`
                                    min-height: 350px;
                                `,
                            ],
                            "min",
                        )}
                }
            `,
        },
    },
    videoModalTitle: {
        css: css`
            margin-top: 16px;
        `,
    },
    videoSliderHeading: {
        variant: "h2",
        css: css`
            justify-content: center;
            display: flex;
            margin-bottom: 24px;
        `,
    },
    iconProps: {},
    carouselContainer: {
        css: css`
            overflow: hidden;
            z-index: 10;
            position: relative;
            margin-top: 16px;
        `,
    },
    slideContainerWrapper: {
        css: css`
            display: flex;
            [aria-hidden="true"] {
                a,
                button {
                    display: none !important;
                }
            }
        `,
    },
    slideContentWrapper: {
        css: css`
            display: flex;
            flex: 0 0 100%;
            width: 100%;
            position: relative;
            background-repeat: no-repeat;
            width: 100%;
            z-index: 10;
        `,
    },
    slideOverlayWrapper: {
        css: css`
            width: 100%;
        `,
    },
    slideCenteringWrapperStyles: {
        css: css`
            width: 100%;
            background: ${themeBackgrounds.lightGray};
        `,
    },
    slideContent: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 24px;
            background: ${themeBackgrounds.lightGray};
        `,
    },
    subheadingText: {
        css: css`
            justify-content: start;
            display: flex;
            font-weight: ${fontWeights.regular};
        `,
    },

    slideContentVideoWrapper: {
        css: css`
            display: block;
        `,
    },
    slideThumbWrapper: {
        css: css`
            position: relative;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            min-height: 175px;
                        `,
                        css`
                            min-height: 175px;
                        `,
                        css`
                            min-height: 400px;
                        `,
                        css`
                            min-height: 400px;
                        `,
                        css`
                            min-height: 397px;
                        `,
                    ],
                    "min",
                )}

            button {
                &,
                > div {
                    width: 100%;
                }
            }

            ${IconWrapper} {
                position: absolute;
                left: 50%;
                top: 50%;
                transform: translate(-50%, -50%);
            }
        `,
    },
    thumbImage: {
        css: css`
            img {
                max-height: 397px;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                min-height: 175px;
                                max-height: 175px;
                            `,
                            css`
                                min-height: 400px;
                                max-height: 400px;
                            `,
                            css`
                                min-height: 400px;
                                max-height: 400px;
                            `,
                            css`
                                min-height: 397px;
                                max-height: 397px;
                            `,
                            css`
                                min-height: 397px;
                                max-height: 397px;
                            `,
                        ],
                        "min",
                    )}

                object-fit: cover;
            }
        `,
    },
    videoSliderButton: {
        variant: "tertiary",
        css: css`
            margin-top: 0px;
        `,
    },
    dotsContainerWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            position: relative;
            width: 100%;
            bottom: 0;
            justify-content: flex-end;
        `,
    },

    nextArrowWrapper: {
        css: css`
            z-index: 0;
        `,
    },
    nextArrowButton: {
        shape: "rectangle",
        css: css`
            padding: 0;
            min-width: auto;
            min-height: auto;
            z-index: 2;
            justify-content: center;
            background: none;
            border: none;

            ${IconWrapper} {
                svg {
                    path {
                        fill: ${themeColors.secondary.danaDarkBlue};
                    }
                }
            }

            &:not(:disabled) {
                &:hover {
                    background: transparent;
                    border: none;

                    ${IconWrapper} {
                        svg {
                            path {
                                fill: ${themeColors.secondary.danaBrightBlue};
                            }
                        }
                    }
                }

                &:focus {
                    outline: none;
                }

                &:focus-visible {
                    background: transparent;
                    border: 1px solid ${themeColors.primary.white};
                    box-shadow: ${elevation.focus};
                    outline: none;

                    ${IconWrapper} {
                        svg {
                            path {
                                fill: ${themeColors.secondary.danaBrightBlue};
                            }
                        }
                    }
                }
            }

            &:disabled {
                background: transparent;
                ${IconWrapper} {
                    svg {
                        path {
                            fill: ${themeBackgrounds.darkGray};
                        }
                    }
                }
            }
        `,
    },
    closeModelIcon: {
        css: css`
            padding: 0;

            min-width: auto;
            min-height: auto;
            z-index: 2;
            justify-content: center;
            background: none;
            border: none;

            ${IconWrapper} {
                svg {
                    height: 16px;
                    width: 16px;
                    path {
                        fill: ${themeColors.primary.richBlack};
                    }
                }
            }
        `,
    },
    carouselArrowWrapper: {
        css: css`
            display: flex;
            column-gap: 8px;
            z-index: 0;
        `,
    },
    videoSliderHeader: {
        css: css`
            text-align: center;
        `,
    },
    viewAllButton: {
        css: css`
            margin-top: 24px;
        `,
    },
};

export const VideoSlider = ({ fields, extendedStyles }: OwnProps) => {
    const { isInShell } = useShellContext();
    const history = useHistory();
    const buttonLinks = useGetLink(fields.buttonLink).url;
    const styles = useMergeStyles("slideshow", slideshowStyles, extendedStyles);

    const [emblaRef, embla] = useEmblaCarousel();
    const [selectedIndex, setSelectedIndex] = useState(0);

    const totalSlides = fields.slides.length;

    const [activeSlide, setActiveSlide] = useState<SlideModel | null>(null);
    const [modalOpen, setModalOpen] = useState(false);

    const openModal = (slide: SlideModel) => {
        if (slide.fields.videoUrl) {
            setActiveSlide(slide);
            setModalOpen(true);
        }
    };

    const closeModal = () => {
        setActiveSlide(null);
        setModalOpen(false);
    };

    useEffect(() => {
        if (!embla) {
            return;
        }

        const onSelect = () => {
            setSelectedIndex(embla.selectedScrollSnap());
        };

        embla.on("select", onSelect);
        onSelect();

        return () => {
            embla.off("select", onSelect);
        };
    }, [embla]);

    const scrollNext = useCallback(() => {
        embla?.scrollNext();
    }, [embla]);

    const scrollPrev = useCallback(() => {
        embla?.scrollPrev();
    }, [embla]);

    const isPrevDisabled = selectedIndex === 0;
    const isNextDisabled = selectedIndex === totalSlides - 1;

    useEffect(() => {
        if (!embla || !fields.slides || fields.slides.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            loop: true,
        });
    }, [embla, fields.slides]);

    const redirectClick = (url: string | undefined) => {
        if (!url) {
            return;
        }

        history.push(url);
    };

    if (!fields.slides || fields.slides.length === 0) {
        return null;
    }

    return (
        <StyledWrapper className="video-slider-container-wrapper" {...styles.videoSliderContainerWrapper}>
            <StyledWrapper {...styles.slideShowContainer} className="video-slider-container">
                <StyledWrapper {...styles.videoSliderHeader}>
                    <Typography {...styles.videoSliderHeading}>{fields.slideTitle}</Typography>
                </StyledWrapper>
                <StyledWrapper className="slideshow-wrapper" {...styles.slideshowWrapper}>
                    {fields.slideIndicator && fields.slides.length > 1 && (
                        <StyledWrapper className="slide-indicator" {...styles.dotsContainerWrapper}>
                            <StyledWrapper {...styles.carouselArrowWrapper}>
                                {fields.slides.length > 1 && (
                                    <StyledWrapper {...styles.prevArrowWrapper}>
                                        <Button
                                            {...styles.prevArrowButton}
                                            onClick={scrollPrev}
                                            aria-label={translate("Previous Arrow")}
                                            disabled={isPrevDisabled}
                                        >
                                            <ButtonIcon {...styles.iconProps} src={PrevArrowIcon} />
                                        </Button>
                                    </StyledWrapper>
                                )}
                                {fields.slides.length > 1 && (
                                    <StyledWrapper className="slide-arrow-wrapper" {...styles.nextArrowWrapper}>
                                        <Button
                                            className="slide-arrow"
                                            {...styles.nextArrowButton}
                                            onClick={scrollNext}
                                            aria-label={translate("Next Arrow")}
                                            disabled={isNextDisabled}
                                        >
                                            <ButtonIcon {...styles.iconProps} src={NextArrowIcon} />
                                        </Button>
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                    <StyledWrapper className="slideshow-carousel" {...styles.carouselContainer} ref={emblaRef}>
                        <StyledWrapper className="slideshow-slide-container" {...styles.slideContainerWrapper}>
                            {fields.slides.map((slide, index) => {
                                const slideWrapperStyles = {
                                    css: css`
                                        ${styles.slideContentWrapper?.css || ""}
                                    `,
                                };

                                return (
                                    <StyledWrapper
                                        className="slide-wrapper"
                                        key={index + Math.random()}
                                        {...slideWrapperStyles}
                                        aria-hidden={index !== selectedIndex ? "true" : "false"}
                                    >
                                        <StyledWrapper {...styles.slideCenteringWrapperStyles}>
                                            <StyledWrapper className="slide-content" {...styles.slideContent}>
                                                <GridItem
                                                    width={12}
                                                    className="slide-content-video-wrapper"
                                                    {...styles.slideContentVideoWrapper}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    {slide.fields.videoUrl && (
                                                        <StyledWrapper {...styles.slideThumbWrapper}>
                                                            <Clickable onClick={() => openModal(slide)}>
                                                                <LazyImage
                                                                    src={slide.fields.videothumbnail}
                                                                    {...styles.thumbImage}
                                                                />
                                                                {slide.fields.videothumbnail && (
                                                                    <Icon size={60} src={CirclePlayVideo} />
                                                                )}
                                                            </Clickable>
                                                        </StyledWrapper>
                                                    )}
                                                </GridItem>

                                                <GridItem width={12} {...styles.slideContentVideoTitleWrapper}>
                                                    {slide.fields.videoTitle && (
                                                        <Typography
                                                            className="slide-subheading text-body-lg"
                                                            {...styles.subheadingText}
                                                        >
                                                            {slide.fields.videoTitle}
                                                        </Typography>
                                                    )}
                                                </GridItem>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                );
                            })}
                        </StyledWrapper>

                        <Modal
                            isOpen={modalOpen}
                            handleClose={closeModal}
                            className="video-modal-content"
                            {...styles.videoModal}
                        >
                            <Typography className="modal-title" {...styles.videoModalTitle}>
                                {activeSlide?.fields.videoTitle}
                            </Typography>

                            <ReactPlayer
                                controls={true}
                                url={activeSlide?.fields.videoUrl}
                                key={activeSlide?.fields.videoUrl}
                                width="100%"
                                height="100%"
                            />
                            <a
                                href="#"
                                onClick={e => {
                                    e.preventDefault();
                                }}
                            >
                                {" "}
                            </a>
                        </Modal>

                        <VisuallyHidden aria-live="polite" aria-atomic="true">
                            {translate("You are currently viewing slide")} {selectedIndex + 1} {translate("of")}{" "}
                            {fields.slides.length}
                        </VisuallyHidden>
                    </StyledWrapper>
                    <StyledWrapper {...styles.viewAllButton}>
                        {(fields.buttonLabel || buttonLinks) && (
                            <Button
                                className="slide-button"
                                {...styles.videoSliderButton}
                                onClick={() => redirectClick(buttonLinks)}
                            >
                                {fields.buttonLabel}
                            </Button>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: VideoSlider,
    definition: {
        group: "Basic",
        icon: "presentation-screen",
        displayName: "Video Slider",
        fieldDefinitions: [
            {
                name: fields.slideIndicator,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: "slideTitle",
                editorTemplate: "TextField",
                defaultValue: "Slide",
                fieldType: "Contextual",
                tab: contentTab,
            },
            {
                name: fields.slides,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: ({ fields: { videoTitle } }) => videoTitle || "Slide",
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: "videoUrl",
                        editorTemplate: "ImagePickerField",
                        displayName: "Video Url",
                        defaultValue: "",
                    },
                    {
                        name: "videothumbnail",
                        editorTemplate: "ImagePickerField",
                        displayName: "Video Thumbnail",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "videoTitle",
                        editorTemplate: "TextField",
                        displayName: "Video Title",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: "buttonLabel",
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
                fieldType: "Contextual",
                tab: contentTab,
            },
            {
                name: "buttonLink",
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "Contextual",
                tab: contentTab,
            },
        ],
    },
};

export default widgetModule;
